import { ROLES } from 'app/auth/authRoles';
import { checkIsGlobalRole } from 'app/auth/authUtils';
import { createSelector } from 'reselect';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { FeatureFlag } from 'constants/featureFlags';
import { PAGE_CODES } from 'constants/pageCodes';
import { getConditionalObject } from 'utils/data';
import { checkIsAdmin } from 'utils/permissions';

import {
  getMasterPermissionsToPage,
  getMasterRole,
  selectImpersonateMode,
  selectIsClaimAllowed,
} from 'store/auth/selectors';
import { selectFeatureFlagByKey, selectRolesListExcludeInternal } from 'store/global/selectors';
import { selectCompanyRoleRestrictions } from 'store/system/selectors/companyRoleRestrictions.selectors';

import { getUser } from './user.selectors';

export const selectUserRoles = createSelector(getUser, ({ roles }) => roles);

export const selectUserRoleValues = createSelector(selectUserRoles, Object.values);

const selectNotChosenRoles = createSelector(
  [getMasterRole, selectRolesListExcludeInternal, selectUserRoles],
  (masterRole, roles, userRoles) => {
    const filteredRoles = roles.filter(({ id, name }) => {
      const userRolesIds = Object.keys(userRoles);

      if (!userRolesIds.includes(id) && name.toLowerCase() === ROLES.ADMIN) {
        return checkIsAdmin(masterRole);
      }

      return !userRolesIds.includes(id);
    });

    return filteredRoles;
  },
);

const flagRestricted = (roles, restrictedRoleIds) => {
  if (!restrictedRoleIds.length) {
    return roles;
  }

  const restrictedRoleIdSet = new Set(restrictedRoleIds);

  return roles.map(role => ({
    ...role,
    isRestricted: checkIsGlobalRole(role) && restrictedRoleIdSet.has(role.id),
  }));
};

export const selectAssignableRoles = createSelector(
  [selectNotChosenRoles, selectCompanyRoleRestrictions],
  flagRestricted,
);

export const selectAssignedRolesWithRestriction = createSelector(
  [selectUserRoleValues, selectCompanyRoleRestrictions],
  flagRestricted,
);

export const getCompanyUsersCells = createSelector(
  [
    selectImpersonateMode,
    selectIsClaimAllowed(COMPOSED_CLAIMS.INTERNAL_AUTH_IMPERSONATE),
    getMasterPermissionsToPage(PAGE_CODES.INTERNAL_COMPANIES),
  ],
  (impersonateMode, isImpersonateAllowed, { isManageAllowed }) => {
    const canImpersonate = isImpersonateAllowed && !impersonateMode;
    const canDelete = isManageAllowed;

    const cells = [
      {
        id: 'impersonate',
        align: 'left',
        disablePadding: false,
        label: '',
        sort: false,
        show: canImpersonate,
        style: { width: '7.5%' },
      },
      {
        id: 'email',
        align: 'left',
        disablePadding: false,
        label: 'Email',
        sort: true,
        show: true,
        style: { width: '22%' },
      },
      {
        id: 'firstName',
        align: 'left',
        disablePadding: false,
        label: 'First name',
        sort: true,
        show: true,
        style: { width: '20%' },
      },
      {
        id: 'lastName',
        align: 'left',
        disablePadding: false,
        label: 'Last name',
        sort: true,
        show: true,
        style: { width: '20%' },
      },
      {
        id: 'isOwner',
        align: 'center',
        disablePadding: false,
        label: 'Owner',
        sort: true,
        show: true,
        style: { width: '14%' },
      },
      {
        id: 'isAuthorizedSigner',
        align: 'center',
        disablePadding: false,
        label: 'Authorized signer',
        sort: true,
        show: true,
        style: { width: '14%' },
      },
      {
        id: 'delete',
        align: 'left',
        disablePadding: false,
        label: '',
        sort: false,
        show: canDelete,
        style: { width: '7.5%' },
      },
    ];

    return cells.filter(cell => cell.show);
  },
);

export const selectUsersCells = createSelector(
  [
    selectImpersonateMode,
    selectIsClaimAllowed(COMPOSED_CLAIMS.INTERNAL_AUTH_IMPERSONATE),
    selectIsClaimAllowed(COMPOSED_CLAIMS.USERS_IMPERSONATE),
    getMasterPermissionsToPage(PAGE_CODES.USERS),
  ],
  (
    impersonateMode,
    canInternalCompanyUserImpersonate,
    canCompanyUserImpersonate,
    { isManageAllowed },
  ) => {
    const isImpersonateAllowed =
      (canInternalCompanyUserImpersonate || canCompanyUserImpersonate) && !impersonateMode;
    const canDelete = isManageAllowed;

    const cells = [
      {
        id: 'impersonate',
        align: 'left',
        disablePadding: false,
        label: '',
        sort: false,
        show: isImpersonateAllowed,
        style: { width: '7.5%' },
      },
      {
        id: 'email',
        align: 'left',
        disablePadding: false,
        label: 'Email',
        sort: true,
        show: true,
        style: { width: '25%' },
      },
      {
        id: 'firstName',
        align: 'left',
        disablePadding: false,
        label: 'First name',
        sort: true,
        show: true,
        style: { width: '15%' },
      },
      {
        id: 'lastName',
        align: 'left',
        disablePadding: false,
        label: 'Last name',
        sort: true,
        show: true,
        style: { width: '15%' },
      },
      {
        id: 'companyName',
        align: 'left',
        disablePadding: false,
        label: 'Company',
        sort: true,
        show: true,
        style: { width: '20%' },
      },
      {
        id: 'delete',
        align: 'left',
        disablePadding: false,
        label: '',
        sort: false,
        show: canDelete,
        style: { width: '7.5%' },
      },
    ];

    return cells.filter(cell => cell.show);
  },
);

export const selectApplicantsCells = createSelector(
  [selectFeatureFlagByKey(FeatureFlag.TurnOnCoPilot)],
  isCoPilotTurnedOn => [
    {
      id: 'email',
      align: 'left',
      disablePadding: false,
      label: 'Email',
      sort: true,
      style: { width: isCoPilotTurnedOn ? '15%' : '30%' },
    },
    {
      id: 'firstName',
      align: 'left',
      disablePadding: false,
      label: 'First name',
      sort: true,
      style: { width: isCoPilotTurnedOn ? '15%' : '30%' },
    },
    {
      id: 'lastName',
      align: 'left',
      disablePadding: false,
      label: 'Last name',
      sort: true,
      style: { width: isCoPilotTurnedOn ? '15%' : '30%' },
    },
    {
      id: 'state',
      align: 'left',
      disablePadding: false,
      label: 'State',
      sort: false,
      style: { width: isCoPilotTurnedOn ? '7.5%' : '10%' },
    },
    getConditionalObject(isCoPilotTurnedOn, {
      id: 'connect',
      align: 'left',
      disablePadding: false,
      label: 'Connect to applicant',
      buttonLabel: 'Connect',
      sort: false,
      style: { width: '12.5%' },
    }),
  ],
);

export const selectRoleUsersCells = createSelector([], () => [
  {
    id: 'isBelongToCurrentPageRole',
    align: 'center',
    disablePadding: false,
    label: 'Security group',
    sort: true,
    style: { width: '10%' },
  },
  {
    id: 'email',
    align: 'left',
    disablePadding: false,
    label: 'Email',
    sort: true,
    style: { width: '25%' },
  },
  {
    id: 'firstName',
    align: 'left',
    disablePadding: false,
    label: 'First name',
    sort: true,
    style: { width: '15%' },
  },
  {
    id: 'lastName',
    align: 'left',
    disablePadding: false,
    label: 'Last name',
    sort: true,
    style: { width: '15%' },
  },
  {
    id: 'companyName',
    align: 'left',
    disablePadding: false,
    label: 'Company',
    sort: true,
    style: { width: '20%' },
  },
]);
